import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  Group,
  ViewList,
  PlaylistPlay,
  Security
} from '@mui/icons-material';
import UserForm from '../components/users/UserForm';
import TeamHierarchy from '../components/users/TeamHierarchy';
import LeadFlowControl from '../components/leadflow/LeadFlowControl';
import ManagerPasswordDialog from '../components/users/ManagerPasswordDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  fetchStates,
  fetchDiseases
} from '../store/slices/usersSlice';
import { authService } from '../services/auth';

const Users = () => {
  const dispatch = useDispatch();
  const { users = [], states = [], diseases = [], loading, error } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchStates());
    dispatch(fetchDiseases());
  }, [dispatch]);

  const handleCreateUser = async (userData) => {
    try {
      await dispatch(createUser(userData)).unwrap();
      setUserFormOpen(false);
      setSelectedUser(null);
      showSnackbar('User created successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to create user', 'error');
    }
  };

  const handleUpdateUser = async (userData) => {
    try {
      await dispatch(updateUser({ id: selectedUser.id, userData })).unwrap();
      setUserFormOpen(false);
      setSelectedUser(null);
      showSnackbar('User updated successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to update user', 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await dispatch(deleteUser(userToDelete.id)).unwrap();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      showSnackbar('User deleted successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to delete user', 'error');
    }
  };

  const handleMenuOpen = (event, userId) => {
    setMenuAnchor(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuUserId(null);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserFormOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangeUserPassword = (user) => {
    setSelectedUserForPassword(user);
    setPasswordDialogOpen(true);
    handleMenuClose();
  };

  const handlePasswordSuccess = (message) => {
    showSnackbar(message, 'success');
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'error',
      'manager': 'warning',
      'team_lead': 'info',
      'bda': 'primary'
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Admin',
      'manager': 'Manager', 
      'team_lead': 'Team Lead',
      'bda': 'BDA'
    };
    return labels[role] || role.toUpperCase();
  };

  const canManageUsers = currentUser?.role === 'admin' || currentUser?.role === 'manager';
  const canControlLeadFlow = currentUser?.role === 'admin' || currentUser?.role === 'manager' || currentUser?.role === 'team_lead';

  if (loading && users.length === 0) {
    return <LoadingSpinner message="Loading team members..." />;
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <div>
          <Typography variant="h4" gutterBottom>
            Team Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your team structure and user roles • {users.length} team member{users.length !== 1 ? 's' : ''}
          </Typography>
        </div>
        
        {canManageUsers && activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setSelectedUser(null);
              setUserFormOpen(true);
            }}
          >
            Add Team Member
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="team management tabs">
          <Tab 
            icon={<ViewList />} 
            label="Team List" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<Group />} 
            label="Team Hierarchy" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          {canControlLeadFlow && (
            <Tab 
              icon={<PlaylistPlay />} 
              label="Lead Flow Control" 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          )}
        </Tabs>
      </Box>

      {error && (
        <ErrorAlert 
          error={error} 
          onClose={() => dispatch({ type: 'users/clearError' })} 
        />
      )}

      {/* Team Statistics */}
      {activeTab === 0 && users.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" color="primary">
                    {users.filter(u => u.role === 'manager').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Managers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" color="info.main">
                    {users.filter(u => u.role === 'team_lead').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Team Leads
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" color="success.main">
                    {users.filter(u => u.role === 'bda').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    BDAs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" color={users.filter(u => !u.is_active).length > 0 ? "error.main" : "text.secondary"}>
                    {users.filter(u => !u.is_active).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
        {Array.isArray(users) && users.map((user) => (
          <Grid item xs={12} sm={6} md={4} key={user.id}>
            <Card sx={{ height: '100%', position: 'relative' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2 }}>
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">
                      {user.first_name} {user.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{user.username}
                    </Typography>
                  </Box>
                  {canManageUsers && (
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuOpen(e, user.id)}
                    >
                      <MoreVert />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {!user.is_active && (
                    <Chip
                      label="Inactive"
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1, mb: 1 }}
                    />
                  )}
                  {!user.lead_flow_enabled && (
                    <Chip
                      label="Lead Flow Paused"
                      color="warning"
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1, mb: 1 }}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>

                {user.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Phone sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.phone}
                    </Typography>
                  </Box>
                )}

                {/* Team Lead Information for BDA users */}
                {user.role === 'bda' && user.team_lead_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Group sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Team Lead: {user.team_lead_name}
                    </Typography>
                  </Box>
                )}

                {/* Created By and Date Information */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Created: {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Typography>
                  {user.created_by_name && (
                    <Typography variant="caption" color="text.secondary">
                      by {user.created_by_name}
                    </Typography>
                  )}
                </Box>

                {user.assigned_states && user.assigned_states.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Assigned States:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.assigned_states.map((state) => (
                        <Chip
                          key={state.id}
                          label={state.name}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {user.assigned_diseases && user.assigned_diseases.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Assigned Diseases:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.assigned_diseases.map((disease) => (
                        <Chip
                          key={disease.id}
                          label={disease.name}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}

        {(!Array.isArray(users) || users.length === 0) && !loading && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Team Members Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Start building your team by adding the first team member.
                </Typography>
                {canManageUsers && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setSelectedUser(null);
                      setUserFormOpen(true);
                    }}
                  >
                    Add Team Member
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        </Grid>
      )}

      {/* Team Hierarchy Tab */}
      {activeTab === 1 && (
        <TeamHierarchy />
      )}

      {/* Lead Flow Control Tab */}
      {canControlLeadFlow && activeTab === 2 && (
        <LeadFlowControl />
      )}

      {/* Floating Action Button for Mobile */}
      {canManageUsers && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
          onClick={() => {
            setSelectedUser(null);
            setUserFormOpen(true);
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(users.find(u => u.id === menuUserId))}>
          <Edit sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        {(currentUser?.role === 'manager' || currentUser?.role === 'admin') && (
          <MenuItem onClick={() => handleChangeUserPassword(users.find(u => u.id === menuUserId))}>
            <Security sx={{ mr: 1 }} />
            Change Password
          </MenuItem>
        )}
        <MenuItem 
          onClick={() => handleDeleteConfirm(users.find(u => u.id === menuUserId))}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* User Form Dialog */}
      <UserForm
        open={userFormOpen}
        onClose={() => {
          setUserFormOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
        user={selectedUser}
        loading={loading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {userToDelete?.first_name} {userToDelete?.last_name}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manager Password Dialog */}
      <ManagerPasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        user={selectedUserForPassword}
        onSuccess={handlePasswordSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;